import CancelIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { BID_ACTIONS } from "../../util/bid/bidConstants";
import { FREEFORM_REASON_CHAR_LIMIT_FOR_BID, NON_CONTRACT } from "../../util/constants";
import { validateBidReasonComments, validateLaborContactTextInput } from "../../util/bid/bidInputValidation";
import { UNKNOWN_ACTION_MESSAGE } from "../../util/messageConstants";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import WarningDialog from "../fundamentals/WarningDialog";
import BidAdminActionConfirm from "./BidAdminActionCheckConfirm";
import { getShiftDate } from "../../util/dateTimeFormatter";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";

const BidAdminActionCheckDialog = (props) => {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isActionError, setIsActionError] = useState(false);
  const [actionErrors, setActionErrors] = useState("");
  const [reasonCommentsTouched, setReasonCommentsTouched] = useState(false);
  const [reasonCommentsErrors, setReasonCommentsErrors] = useState("");
  const [reasonComments, setReasonComments] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [isLaborContactSelected, setIsLaborContactSelected] = useState(false);
  const [laborContactCheckBoxError, setLaborContactCheckBoxError] = useState("");
  const [laborContact, setLaborContact] = useState("");
  const [laborContactErrors, setLaborContactErrors] = useState("");

  useEffect(() => {
    initialValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValidation = () => {
    let validationMessage = "";
    // TODO:

    return validationMessage;
  };

  const isFormValidationSuccess = () => {
    let validationMessage = initialValidation();
    let reasonCommentsErrors = "";

    reasonCommentsErrors = validateBidReasonComments(reasonComments, isReasonCommentsRequired);

    setWarningMessage(validationMessage);
    setReasonCommentsTouched(true);
    setReasonCommentsErrors(reasonCommentsErrors);
    if (
      props.actionSelected === BID_ACTIONS.WITHDRAW &&
      getUnionContractGivenWorkgroup(props.bid.workgroup) !== NON_CONTRACT
    ) {
      let laborContactErrors = "";
      laborContactErrors = validateLaborContactTextInput(laborContact);
      setLaborContactErrors(laborContactErrors);

      if (!isLaborContactSelected) {
        setLaborContactCheckBoxError("Please confirm you have contacted your Labor Manager");
      }

      return (
        validationMessage === "" && reasonCommentsErrors === "" && laborContactErrors === "" && isLaborContactSelected
      );
    }

    return validationMessage === "" && reasonCommentsErrors === "";
  };

  const isReasonCommentsRequired = () => {
    return true;
  };

  const handleCheckboxSelectionChange = (event) => {
    const isChecked = event.target.checked;
    setIsLaborContactSelected(isChecked);
    if (isChecked) {
      setLaborContactCheckBoxError("");
    }
  };

  const handleLaborContactTextFieldChange = (event) => {
    setLaborContact(event.target.value);
  };

  const handleLaborContactTextFieldBlur = (event) => {
    setLaborContactErrors(validateLaborContactTextInput(event.target.value, isReasonCommentsRequired()));
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    clearDialogState();
    props.onClickDialogClose(event, reason);
  };

  const clearDialogState = () => {
    setIsActionLoading(false);
    setIsActionError(false);
    setActionErrors("");
  };

  const handleSetIsActionError = (isActionError) => {
    setIsActionError(isActionError);
  };

  const handleSetActionErrors = (actionErrors) => {
    setActionErrors(actionErrors);
  };

  const handleSetIsActionLoading = (isActionLoading) => {
    setIsActionLoading(isActionLoading);
  };

  const handleSetIsActionSuccess = (isActionSuccess) => {
    if (isActionSuccess && props.onTimeOffRequestDialogClose) {
      props.onBidActionDialogClose();
    }
  };

  const handleReasonCommentsChange = (event) => {
    setReasonComments(event.target.value);
    setReasonCommentsTouched(true);
  };

  const handleReasonCommentsBlur = (event) => {
    setReasonCommentsErrors(validateBidReasonComments(event.target.value, isReasonCommentsRequired()));
    setReasonCommentsTouched(true);
  };

  const handleCommentsClear = (event) => {
    setReasonComments("");
    setReasonCommentsErrors(validateBidReasonComments("", isReasonCommentsRequired()));
    setReasonCommentsTouched(true);
  };

  const renderReasonCommentsDialog = (action) => {
    const CancelCommentsEndAdornment = () => {
      return (
        <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
          <CancelIcon />
        </IconButton>
      );
    };
    return (
      <Grid>
        <Box mt={2} mb={1}>
          <TextField
            label="Comments"
            variant="outlined"
            fullWidth
            value={reasonComments}
            onChange={handleReasonCommentsChange}
            onBlur={handleReasonCommentsBlur}
            helperText={reasonCommentsErrors}
            InputProps={{
              endAdornment:
                reasonComments !== "" ? (
                  <InputAdornment position="start">
                    <CancelCommentsEndAdornment />
                  </InputAdornment>
                ) : (
                  ""
                ),
            }}
            multiline
            error={reasonCommentsTouched && reasonCommentsErrors !== ""}
          />
          {reasonComments.length > FREEFORM_REASON_CHAR_LIMIT_FOR_BID ? (
            <Box sx={{ color: "primary.swaRed", textAlign: "right" }}>
              {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
            </Box>
          ) : (
            <Box sx={{ textAlign: "right" }}>
              {reasonComments.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  const renderLaborContactDetails = () => {
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox checked={isLaborContactSelected} onChange={handleCheckboxSelectionChange} color="primary" />
          }
          label="Have you contacted your Labor Manager?"
        />
        {laborContactCheckBoxError !== "" && <FormHelperText error={true}>{laborContactCheckBoxError}</FormHelperText>}

        <TextField
          sx={{ marginTop: "10px" }}
          variant="outlined"
          label="Labor Contact"
          value={laborContact}
          onChange={handleLaborContactTextFieldChange}
          onBlur={handleLaborContactTextFieldBlur}
          helperText={laborContactErrors}
          error={laborContactErrors !== ""}
        />
      </>
    );
  };

  const renderDialogDetails = () => {
    switch (props.actionSelected) {
      case BID_ACTIONS.DELETE:
        return renderReasonCommentsDialog(BID_ACTIONS.DELETE);
      case BID_ACTIONS.WITHDRAW:
        return renderReasonCommentsDialog(BID_ACTIONS.WITHDRAW);
      default:
        return UNKNOWN_ACTION_MESSAGE(props.actionSelected);
    }
  };

  const getSchedDates = () => {
    const { schedStartDate, schedEndDate } = props.bid;
    if (!schedStartDate || !schedEndDate) return "";

    const start = getShiftDate(schedStartDate);
    const end = getShiftDate(schedEndDate);
    return `${start} to ${end}`;
  };

  const renderTitle = () => {
    const { actionSelected, bid } = props;
    const { station, workgroup, bidName } = bid;
    if (actionSelected === BID_ACTIONS.WITHDRAW || actionSelected === BID_ACTIONS.DELETE) {
      const schedDates = getSchedDates();
      return (
        <DialogTitle id="alert-dialog-slide-title">
          {`${station} ${workgroup}${schedDates ? " " + schedDates : ""}${bidName ? " " + bidName : ""}`}
          <br />
          <br />
          Confirm
        </DialogTitle>
      );
    }
    return <DialogTitle id="alert-dialog-slide-title">Confirm</DialogTitle>;
  };

  return (
    <>
      {warningMessage !== "" ? (
        <WarningDialog
          isDialogOpen={props.isDialogOpen}
          handleClickDialogClose={handleClickDialogClose}
          header="Warning"
          message={warningMessage}
          severity={"error"}
        />
      ) : (
        <Dialog
          className="action-check-dialog"
          data-testid="action-check-dialog"
          fullWidth
          maxWidth="md"
          open={props.isDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          {renderTitle()}
          <DialogContent>
            <Grid container>
              <Grid item xs={12}>
                <DialogContentText component={"div"} id="alert-dialog-slide-description">
                  <Grid sx={{ marginTop: "10px" }} xs={12} className="action-check-dialog-question">
                    {`Are you sure you want to ${props.actionSelected} this bid? `}
                  </Grid>
                  {props.actionSelected === BID_ACTIONS.WITHDRAW &&
                    getUnionContractGivenWorkgroup(props.bid.workgroup) !== NON_CONTRACT &&
                    renderLaborContactDetails()}
                  {renderDialogDetails()}
                  {isActionError ? <Alert severity="error">{actionErrors}</Alert> : ""}
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              id="cancelButton"
              variant="outlined"
              color="primary"
              onClick={handleClickDialogClose}
              disabled={isActionLoading}
              size="large"
            >
              Cancel
            </Button>
            <BidAdminActionConfirm
              bid={props.bid}
              actionSelected={props.actionSelected}
              onClickDialogClose={handleClickDialogClose}
              onSetIsActionError={handleSetIsActionError}
              onSetActionErrors={handleSetActionErrors}
              onSetIsActionLoading={handleSetIsActionLoading}
              onSetIsActionSuccess={handleSetIsActionSuccess}
              isActionLoading={isActionLoading}
              laborContact={laborContact}
              reasonComments={reasonComments}
              isFormValidationSuccess={isFormValidationSuccess}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

BidAdminActionCheckDialog.propTypes = {
  bid: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  // onBidActionDialogClose: PropTypes.func.isRequired,
};

export default BidAdminActionCheckDialog;
